/**
 * A conversa do Assistente Comercial.
 *
 * UM componente para os dois lugares: o bloco em destaque no painel inicial e o
 * painel lateral das demais telas. `variante` muda o enquadramento (altura,
 * cabeçalho), nunca o comportamento — duas implementações divergiriam no primeiro
 * ajuste, e a pessoa encontraria um assistente diferente dependendo de onde
 * clicou.
 *
 * O CAMPO DE TEXTO É O ELEMENTO PRINCIPAL
 *
 * Sempre visível, nunca atrás de ícone, tooltip ou submenu. É o requisito
 * central: quem entra no CRM precisa PERCEBER que pode conversar, sem procurar.
 * Por isso o campo aparece já no primeiro render, antes de qualquer conversa
 * existir, com um exemplo real no placeholder.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  IconCheck,
  IconClose,
  IconSpark,
  Spinner,
} from './ui';
import {
  useAssistente,
  type Indisponibilidade,
  type Opcao,
  type PassoExecutado,
  type PlanoApresentado,
  type Previa,
  type Retomada as RetomadaDaConversa,
  type FonteDaResposta,
} from '../lib/assistente';
import { useVoz } from '../hooks/useVoz';
import { api } from '../lib/api';
import { t as traduzir } from '../lib/i18n';

interface Props {
  variante: 'painel-inicial' | 'lateral';
  aoFechar?: () => void;
}

/** Rota do registro criado, para o link de "abrir" depois da execução. */
function rotaDoRegistro(tipo: string, id: string): string | null {
  const mapa: Record<string, string> = {
    account: `/empresas/${id}`,
    contact: `/contatos/${id}`,
    opportunity: `/oportunidades/${id}`,
  };
  return mapa[tipo] ?? null;
}

function BlocoDePrevia({ previa }: { previa: Previa }): JSX.Element {
  return (
    <div className="mt-2 rounded border border-line bg-surface-sunken p-2.5">
      <p className="text-xs font-semibold text-ink">{previa.titulo}</p>
      {previa.campos.length > 0 && (
        <dl className="mt-1.5 space-y-0.5">
          {previa.campos.map((c) => (
            <div key={c.rotulo} className="flex gap-2 text-2xs">
              <dt className="shrink-0 text-ink-subtle">{c.rotulo}</dt>
              <dd className="min-w-0 flex-1 truncate text-ink">
                {c.de !== null && c.de !== c.para && (
                  <span className="text-ink-subtle line-through">{c.de} → </span>
                )}
                {c.para ?? '—'}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {previa.avisos.length > 0 && (
        <ul className="mt-1.5 space-y-0.5">
          {previa.avisos.map((a) => (
            <li key={a} className="text-2xs text-warning-700">
              {a}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * O RÓTULO DO PASSO VEM DO SERVIDOR (§20.3).
 *
 * Aqui havia uma segunda cópia do mapa de tipos de ação, com `?? p.tipo` de
 * reserva. Dois mapas para a mesma lista fechada divergem no primeiro tipo novo,
 * e o que diverge aparecia cru — `criar_empresa` — justamente na tela em que
 * alguém autoriza escrita. Agora a tela mostra o que recebeu, e a reserva
 * ('Passo do plano') é genérica de propósito: um defeito visível é melhor do que
 * um que parece funcionar.
 */
const RESERVA_DO_PASSO = 'Passo do plano';

/**
 * O PLANO, com Confirmar / Corrigir / Cancelar.
 *
 * Antes isto voltava como três sugestões de texto no meio das outras. Uma
 * autorização de escrita não pode parecer um atalho de digitação: os passos são
 * enumerados, os de escrita ficam marcados, e as três saídas são botões.
 */
function BlocoDePlano({
  plano,
  ativo,
  termoExigido,
  onConfirmar,
  onCorrigir,
  onCancelar,
}: {
  plano: PlanoApresentado;
  /** Só o plano do último turno aceita ação; os anteriores viram histórico. */
  ativo: boolean;
  /**
   * Termo da confirmação FORTE. Com ele, não há botão "Confirmar": o botão
   * mandava "confirmar", que o servidor recusa nesse nível — a tela oferecia um
   * caminho que não funciona. A confirmação forte é digitar o termo no campo.
   */
  termoExigido: string | null;
  onConfirmar: () => void;
  onCorrigir: () => void;
  onCancelar: () => void;
}): JSX.Element {
  return (
    <div className="mt-2 rounded-md border border-brand-200 bg-surface p-2">
      <p className="text-2xs font-semibold uppercase tracking-wide text-ink-subtle">
        O que eu entendi
      </p>
      <ol className="mt-1 space-y-1">
        {plano.passos.map((p, i) => (
          <li key={p.id} className="flex items-start gap-1.5 text-2xs text-ink">
            <span className="mt-px font-mono text-ink-subtle">{i + 1}.</span>
            <span>
              {p.rotulo === '' ? RESERVA_DO_PASSO : p.rotulo}
              {p.escrita && (
                <span className="ml-1 rounded bg-warning-50 px-1 text-2xs text-warning-700">
                  grava
                </span>
              )}
            </span>
          </li>
        ))}
      </ol>
      {ativo && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {termoExigido === null ? (
            <button type="button" className="btn-primary btn-sm" onClick={onConfirmar}>
              Confirmar
            </button>
          ) : (
            <p className="w-full text-2xs text-warning-700">
              {traduzir('assistente.confirmeComOTermo')} <strong>{termoExigido}</strong>
            </p>
          )}
          <button type="button" className="btn-secondary btn-sm" onClick={onCorrigir}>
            Corrigir
          </button>
          <button type="button" className="btn-ghost btn-sm" onClick={onCancelar}>
            Cancelar
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * O RESULTADO, passo a passo.
 *
 * Numa falha no meio, "deu erro" deixa a pessoa sem saber se refaz tudo. Aqui
 * cada etapa mostra o que foi gravado, o que foi pulado e onde parou — com link
 * para o que já existe.
 */
function BlocoDePassos({
  passos,
  onConsultar,
}: {
  passos: PassoExecutado[];
  /** Etapa em conferência: consultar o desfecho real pelo pedido dela. */
  onConsultar?: (requestId: string) => void;
}): JSX.Element {
  const marca: Record<string, string> = {
    concluido: 'text-positive-700',
    falhou: 'text-danger-700',
    pulado: 'text-ink-subtle',
    pendente: 'text-warning-700',
    nao_tentado: 'text-ink-subtle',
    desconhecido: 'text-warning-700',
  };
  const simbolo: Record<string, string> = {
    concluido: '✓',
    falhou: '✗',
    pulado: '•',
    pendente: '…',
    nao_tentado: '○',
    desconhecido: '?',
  };
  // O símbolo é decorativo: o estado vai por EXTENSO para o leitor de tela. "✗"
  // lido como "multiplicação" não diz que a etapa falhou.
  const estadoPorExtenso: Record<string, string> = {
    concluido: traduzir('assistente.passoConcluido'),
    falhou: traduzir('assistente.passoFalhou'),
    pulado: traduzir('assistente.passoPulado'),
    pendente: traduzir('assistente.passoPendente'),
    nao_tentado: traduzir('assistente.passoNaoTentado'),
    desconhecido: traduzir('assistente.passoEmConferencia'),
  };
  return (
    <ul className="mt-2 space-y-1 border-t border-line pt-2">
      {passos.map((p) => {
        const rota = p.registro === null ? null : rotaDoRegistro(p.registro.tipo, p.registro.id);
        return (
          <li key={p.id} className="text-2xs">
            <span aria-hidden="true" className={`font-mono ${marca[p.estado] ?? 'text-ink'}`}>
              {simbolo[p.estado] ?? '·'}
            </span>{' '}
            <span className="sr-only">{estadoPorExtenso[p.estado] ?? p.estado}: </span>
            <span className="text-ink">
              {p.resumo === '' ? (p.rotulo ?? RESERVA_DO_PASSO) : p.resumo}
            </span>
            {p.motivo !== null && <span className="text-ink-subtle"> — {p.motivo}</span>}
            {/*
              O PRÓXIMO PASSO, quando ele existe.
              Uma falha que não diz o que fazer devolve o problema para quem
              pediu. `oQueFazer` só vem preenchido quando há uma ação concreta —
              e quando não há, nada é impresso, em vez de uma frase de consolo.
            */}
            {p.falha?.oQueFazer != null && (
              <div className="mt-0.5 text-ink-subtle">{p.falha.oQueFazer}</div>
            )}
            {rota !== null && (
              <Link to={rota} className="ml-1 font-medium text-brand-700 underline">
                abrir
              </Link>
            )}
            {p.estado === 'desconhecido' && p.requestId != null && onConsultar !== undefined && (
              <button
                type="button"
                className="ml-1 font-medium text-brand-700 underline"
                onClick={() => onConsultar(p.requestId!)}
              >
                {traduzir('assistente.consultarOperacao')}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * A INDISPONIBILIDADE, com saída.
 *
 * O que estava errado antes: uma frase educada e um beco sem saída. O pedido
 * sumia do campo, não havia o que clicar, e as telas do CRM pareciam ter caído
 * junto — quando na verdade só a interpretação estava fora.
 *
 * Aqui a pessoa tem duas saídas reais e um código curto para o suporte. O que
 * NÃO existe é o assistente fingir que entendeu: no modo guiado ele para de
 * interpretar e passa a oferecer ações nomeadas.
 */
function BlocoDeIndisponibilidade({
  dados,
  ativo,
  onTentarDeNovo,
  onGuiado,
}: {
  dados: Indisponibilidade;
  ativo: boolean;
  onTentarDeNovo: () => void;
  onGuiado: (chave: string) => void;
}): JSX.Element {
  const [escolhendo, setEscolhendo] = useState(false);
  const aproveitados = Object.entries(dados.camposAproveitados);

  return (
    <div className="mt-2 rounded-md border border-warning-200 bg-warning-50 p-2">
      <p className="text-2xs font-semibold uppercase tracking-wide text-warning-700">
        Modo inteligente indisponível
      </p>
      <p className="mt-1 text-2xs text-ink">
        As telas e os cadastros do CRM continuam funcionando normalmente.
      </p>

      {ativo && !escolhendo && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          <button type="button" className="btn-primary btn-sm" onClick={onTentarDeNovo}>
            Tentar novamente
          </button>
          <button type="button" className="btn-secondary btn-sm" onClick={() => setEscolhendo(true)}>
            Continuar em modo guiado
          </button>
        </div>
      )}

      {ativo && escolhendo && (
        <div className="mt-2">
          <p className="text-2xs text-ink-muted">O que você quer fazer?</p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {dados.acoesGuiadas.map((a) => (
              <button
                key={a.chave}
                type="button"
                className="btn-secondary btn-sm"
                title={a.descricao}
                onClick={() => onGuiado(a.chave)}
              >
                {a.rotulo}
              </button>
            ))}
          </div>
          {/* Só o que é inequívoco. Nome de empresa e de pessoa NÃO entram: foi
              extração por regra que produziu os erros que motivaram tudo isto. */}
          {aproveitados.length > 0 && (
            <p className="mt-1.5 text-2xs text-ink-subtle">
              Já anotei do seu texto: {aproveitados.map(([k, v]) => `${k} ${v}`).join(' · ')}. O resto eu
              pergunto.
            </p>
          )}
        </div>
      )}

      <p className="mt-2 text-2xs text-ink-subtle">
        Código para o suporte: <span className="font-mono">{dados.incidente}</span>
      </p>
    </div>
  );
}

/**
 * A PENDÊNCIA DE UMA CONVERSA REABERTA (§20.3).
 *
 * Retomar é o momento em que a pessoa perdeu o contexto — é justamente aí que
 * ela precisa ler o que ficou em aberto. E a pergunta volta LITERAL: reescrevê-la
 * na tela produziria duas perguntas diferentes para o mesmo campo, dependendo de
 * a pessoa ter recarregado ou não.
 */
function BlocoDeRetomada({ retomada }: { retomada: RetomadaDaConversa }): JSX.Element {
  const vencido = retomada.estado === 'EXPIRADO';
  return (
    <div
      className={`mt-3 rounded-md border p-2.5 ${vencido ? 'border-warning-200 bg-warning-50' : 'border-line bg-surface-sunken'}`}
      role="status"
    >
      <p className="text-2xs font-semibold uppercase tracking-wide text-ink-subtle">
        {vencido ? 'O plano venceu' : 'De onde paramos'}
      </p>
      {retomada.pendencia !== null && <p className="mt-1 text-2xs text-ink">{retomada.pendencia}</p>}
      {retomada.pergunta !== null && (
        <p className="mt-1 text-xs font-medium text-ink">{retomada.pergunta}</p>
      )}
    </div>
  );
}

/**
 * FEEDBACK DO TURNO (§20.3, §12.6).
 *
 * A rota existia desde a §12.6 — com caso de aprendizado, curadoria e trilha — e
 * nenhuma tela a alcançava. O ciclo estava fechado por falta de um botão: quem
 * corrigia o assistente corrigia para si mesmo.
 *
 * O feedback é do TURNO, e não da conversa: "o assistente errou" sem dizer em
 * quê não ensina nada, e é o turno que tem o texto do erro.
 *
 * A frase que aparece depois é a do SERVIDOR. "Obrigado, vou melhorar" promete
 * um aprendizado que muitas vezes não houve — e quem descobre isso depois não
 * escreve mais nada.
 */
function BlocoDeFeedback({
  assunto,
  conversationId,
}: {
  assunto: string;
  conversationId: string | null;
}): JSX.Element {
  const [corrigindo, setCorrigindo] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [destino, setDestino] = useState<string | null>(null);
  const [falhou, setFalhou] = useState(false);
  const [enviando, setEnviando] = useState(false);
  /*
   * FOCO (CERES-IA-002). "Não ajudou" some ao ser clicado e dá lugar ao campo:
   * sem levar o foco junto, ele caía no `body` e quem usa teclado recomeçava do
   * topo da página. Abrir leva ao campo; "Deixar para depois" devolve ao botão.
   */
  const campoRef = useRef<HTMLTextAreaElement>(null);
  const naoAjudouRef = useRef<HTMLButtonElement>(null);
  const voltarAoBotao = useRef(false);
  useEffect(() => {
    if (corrigindo) {
      campoRef.current?.focus();
    } else if (voltarAoBotao.current) {
      voltarAoBotao.current = false;
      naoAjudouRef.current?.focus();
    }
  }, [corrigindo]);

  const registrar = async (tipo: 'APROVACAO' | 'REJEICAO', texto: string | null): Promise<void> => {
    setEnviando(true);
    setFalhou(false);
    try {
      const r = await api.post<{ destino: string }>('/agent/feedback', {
        tipo,
        referenciaTipo: 'agent_message',
        ...(conversationId === null ? {} : { referenciaId: conversationId }),
        assunto: assunto.slice(0, 2000),
        ...(texto === null || texto.trim() === '' ? {} : { motivo: texto.trim() }),
      });
      setDestino(r.destino);
      setCorrigindo(false);
    } catch {
      // Registrar feedback é importante; é menos importante do que o trabalho
      // que a pessoa estava fazendo. A conversa fica intacta e o botão volta.
      setFalhou(true);
    } finally {
      setEnviando(false);
    }
  };

  if (destino !== null) {
    return <p className="mt-2 text-2xs text-ink-subtle">{destino}</p>;
  }

  return (
    <div className="mt-2">
      {!corrigindo && (
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            className="btn-ghost btn-sm"
            disabled={enviando}
            onClick={() => void registrar('APROVACAO', null)}
          >
            {traduzir('assistente.feedbackAjudou')}
          </button>
          {/* "Não ajudou", e não "Corrigir": o plano tem o próprio "Corrigir"
              (refazer o pedido), e dois botões com o mesmo nome no mesmo turno
              faziam corrigir a resposta quando a pessoa queria corrigir o plano. */}
          <button
            ref={naoAjudouRef}
            type="button"
            className="btn-ghost btn-sm"
            disabled={enviando}
            onClick={() => setCorrigindo(true)}
          >
            {traduzir('assistente.feedbackNaoAjudou')}
          </button>
        </div>
      )}

      {corrigindo && (
        <div className="space-y-1.5">
          <label className="block text-2xs text-ink-muted">
            {traduzir('assistente.feedbackPergunta')}
            <textarea
              ref={campoRef}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={2}
              className="input mt-1 w-full resize-none py-1.5 text-xs"
            />
          </label>
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              className="btn-secondary btn-sm"
              // Sem motivo, a correção é só um voto negativo — e voto negativo
              // não diz o que fazer diferente.
              disabled={enviando || motivo.trim() === ''}
              onClick={() => void registrar('REJEICAO', motivo)}
            >
              {traduzir('assistente.feedbackEnviar')}
            </button>
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() => {
                voltarAoBotao.current = true;
                setCorrigindo(false);
              }}
            >
              {traduzir('assistente.feedbackDepois')}
            </button>
          </div>
        </div>
      )}

      {falhou && (
        <p className="mt-1 text-2xs text-warning-700">
          Não consegui registrar seu retorno agora. A conversa continua; tente de novo quando quiser.
        </p>
      )}
    </div>
  );
}

/**
 * DE ONDE ISTO VEIO (§20.3).
 *
 * O registro citado vira LINK: citar sem link é pedir para a pessoa procurar na
 * mão o que o sistema já tinha em mãos. E o que veio de fora aparece marcado —
 * sem a marca, um dado de página pública fica com o mesmo peso de um dado do
 * CRM, que é justamente a confusão que a §8.4 existe para evitar.
 *
 * Sem fonte, este bloco não aparece: "De onde isto veio: —" daria lastro visual
 * a uma frase que não tem lastro nenhum.
 */
function BlocoDeFontes({
  fontes,
  ocultas,
}: {
  fontes: FonteDaResposta[];
  ocultas: number;
}): JSX.Element | null {
  if (fontes.length === 0) return null;

  return (
    <div className="mt-2 border-t border-line pt-2">
      <p className="text-2xs font-semibold uppercase tracking-wide text-ink-subtle">
        De onde isto veio
      </p>
      <ul className="mt-1 space-y-0.5">
        {fontes.map((f, i) => {
          const rota =
            f.entidade === null ? null : rotaDoRegistro(f.entidade.tipo, f.entidade.id);
          return (
            <li key={`${f.tipo}-${f.entidade?.id ?? f.rotulo}-${i}`} className="text-2xs text-ink-muted">
              {rota === null ? (
                <span>{f.rotulo}</span>
              ) : (
                <Link to={rota} className="text-brand-700 underline">
                  {f.rotulo}
                </Link>
              )}
              {!f.verificado && (
                <span className="ml-1 rounded bg-warning-50 px-1 text-warning-700">
                  não verificado
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {ocultas > 0 && (
        // Cortar em silêncio faria a resposta parecer baseada em menos do que foi.
        <p className="mt-1 text-2xs text-ink-subtle">e mais {ocultas} fonte(s) consultada(s).</p>
      )}
    </div>
  );
}

export function Assistente({ variante, aoFechar }: Props): JSX.Element {
  const {
    turnos,
    enviando,
    sugestoes,
    opcoes,
    termoExigido,
    estado,
    etapa,
    enviar,
    seguirGuiado,
    textoARestaurar,
    limparTextoARestaurar,
    novaConversa,
    retomada,
    plano: planoPendente,
    conversationId,
    consultarOperacao,
  } = useAssistente();

  /** Só o último turno responde aos botões; os anteriores são histórico. */
  const ultimoTurnoId = turnos.length === 0 ? null : turnos[turnos.length - 1]!.id;

  const [texto, setTexto] = useState('');
  /** Transcrição bruta, guardada quando a pessoa edita o que foi ditado. */
  const [transcricao, setTranscricao] = useState<string | null>(null);
  const campoRef = useRef<HTMLTextAreaElement>(null);
  const fimRef = useRef<HTMLDivElement>(null);

  /**
   * O texto volta ao campo depois de uma falha do servidor.
   *
   * Fazer alguém redigitar um pedido de três linhas porque a inteligência caiu é
   * cobrar da pessoa um erro que não foi dela.
   */
  useEffect(() => {
    if (textoARestaurar === null) return;
    setTexto(textoARestaurar);
    limparTextoARestaurar();
  }, [textoARestaurar, limparTextoARestaurar]);

  const voz = useVoz((reconhecido) => {
    // Vai para o CAMPO, e não para o envio: reconhecimento erra nome próprio, e
    // a pessoa precisa poder corrigir antes de confirmar qualquer coisa.
    setTexto(reconhecido);
    setTranscricao(reconhecido);
    campoRef.current?.focus();
  });

  /** Gravando inclui o instante entre o clique e o microfone realmente abrir. */
  const gravando = voz.estado === 'OUVINDO' || voz.estado === 'PEDINDO_PERMISSAO';

  useEffect(() => {
    fimRef.current?.scrollIntoView({ block: 'end' });
  }, [turnos.length, enviando]);

  const submeter = (e: FormEvent): void => {
    e.preventDefault();
    const valor = texto.trim();
    if (valor === '' || enviando) return;
    // A transcrição original só acompanha se o texto veio mesmo do ditado.
    void enviar(valor, transcricao !== null && transcricao !== valor ? transcricao : null);
    setTexto('');
    setTranscricao(null);
  };

  const escolher = (opcao: Opcao): void => {
    void enviar(opcao.valor);
  };

  const alturaLista =
    variante === 'painel-inicial' ? 'max-h-64 min-h-[3rem]' : 'flex-1 min-h-0';

  return (
    <section
      className={
        variante === 'painel-inicial'
          ? 'rounded-lg border border-line bg-surface p-4'
          : 'flex h-full flex-col bg-surface'
      }
      aria-label="Assistente Comercial"
    >
      <header
        className={
          variante === 'lateral'
            ? 'flex h-14 shrink-0 items-center gap-2 border-b border-line px-4'
            : 'flex items-center gap-2'
        }
      >
        <span className="flex h-7 w-7 items-center justify-center rounded bg-brand-100 text-brand-700">
          <IconSpark className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-sm font-semibold text-ink">Assistente Comercial</h2>
          {variante === 'painel-inicial' && turnos.length === 0 && (
            <p className="text-2xs text-ink-subtle">
              Peça consultas e ações em português — eu executo no CRM e mostro o resultado.
            </p>
          )}
        </div>
        {turnos.length > 0 && (
          <button type="button" className="btn-ghost btn-sm" onClick={novaConversa}>
            {traduzir('assistente.novaConversa')}
          </button>
        )}
        {/* O histórico aparece também no celular (CERES-IA-002): ele estava
            escondido abaixo de `sm`, e quem usa o CRM pelo telefone não tinha
            como voltar a uma conversa. Na própria página de histórico, o link
            seria para onde a pessoa já está. */}
        {variante === 'lateral' && (
          <Link to="/assistente" className="btn-ghost btn-sm" onClick={aoFechar}>
            {traduzir('assistente.historico')}
          </Link>
        )}
        {aoFechar !== undefined && (
          <button type="button" className="btn-ghost btn-sm" onClick={aoFechar} aria-label={traduzir('assistente.fechar')}>
            <IconClose />
          </button>
        )}
      </header>

      {/* ---- conversa ---- */}
      {turnos.length > 0 && (
        <div
          // As respostas chegam sem a pessoa pedir foco: sem região viva, o leitor
          // de tela não anuncia nem o resultado nem o "não sei se concluiu".
          role="log"
          aria-live="polite"
          aria-label={traduzir('assistente.historicoDaConversa')}
          className={`overflow-y-auto scroll-slim ${alturaLista} mt-3 space-y-2 px-0.5 ${variante === 'lateral' ? 'px-4' : ''}`}
        >
          {turnos.map((t) => (
            <div
              key={t.id}
              className={t.papel === 'USER' ? 'flex justify-end' : 'flex justify-start'}
            >
              <div
                className={
                  t.papel === 'USER'
                    ? 'max-w-[85%] rounded-lg bg-brand-600 px-3 py-1.5 text-xs text-ink-inverse'
                    : `max-w-[92%] rounded-lg px-3 py-1.5 text-xs ${
                        t.desconhecido === true
                          ? 'border border-warning-200 bg-warning-50 text-warning-700'
                          : t.erro === true
                            ? 'bg-danger-50 text-danger-700'
                            : 'bg-surface-sunken text-ink'
                      }`
                }
              >
                <p className="whitespace-pre-wrap">{t.texto}</p>

                {t.previa != null && <BlocoDePrevia previa={t.previa} />}

                {t.plano != null && (
                  <BlocoDePlano
                    plano={t.plano}
                    ativo={t.id === ultimoTurnoId && estado === 'AGUARDANDO_CONFIRMACAO'}
                    termoExigido={termoExigido}
                    onConfirmar={() => void enviar('confirmar')}
                    onCorrigir={() => void enviar('corrigir')}
                    onCancelar={() => void enviar('cancelar')}
                  />
                )}

                {t.passos != null && t.passos.length > 0 && (
                  <BlocoDePassos passos={t.passos} onConsultar={(id) => void consultarOperacao(id)} />
                )}

                {/* RESULTADO EM CONFERÊNCIA (CERES-IA-002): nem "feito" nem
                    "falhou". A saída é consultar a operação — pelo pedido,
                    quando se sabe qual é; senão, relendo a conversa. */}
                {t.desconhecido === true && t.id === ultimoTurnoId && !enviando && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="text-2xs font-medium">{traduzir('assistente.emConferencia')}</span>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => void consultarOperacao(t.desfecho?.requestId ?? null)}
                    >
                      {traduzir('assistente.consultarOperacao')}
                    </button>
                  </div>
                )}

                {t.indisponibilidade != null && (
                  <BlocoDeIndisponibilidade
                    dados={t.indisponibilidade}
                    ativo={t.id === ultimoTurnoId}
                    onTentarDeNovo={() => void enviar(t.indisponibilidade!.textoPreservado)}
                    onGuiado={(chave) =>
                      void seguirGuiado(chave, t.indisponibilidade!.textoPreservado)
                    }
                  />
                )}

                {t.guiado === true && (
                  <p className="mt-1 text-2xs font-medium text-warning-700">Modo guiado</p>
                )}

                {t.fontes != null && (
                  <BlocoDeFontes fontes={t.fontes} ocultas={t.fontesOcultas ?? 0} />
                )}

                {/* O retorno fica no turno do ASSISTENTE, e só quando há o que
                    avaliar: um erro de rede não é um erro de julgamento. */}
                {t.papel === 'ASSISTANT' && t.erro !== true && t.desconhecido !== true && (
                  <BlocoDeFeedback assunto={t.texto} conversationId={conversationId} />
                )}

                {t.registro != null && rotaDoRegistro(t.registro.tipo, t.registro.id) !== null && (
                  <Link
                    to={rotaDoRegistro(t.registro.tipo, t.registro.id)!}
                    className="mt-2 inline-flex items-center gap-1 text-2xs font-medium text-brand-700 underline"
                  >
                    <IconCheck className="h-3 w-3" /> Abrir o registro
                  </Link>
                )}
              </div>
            </div>
          ))}
          {enviando && (
            <div className="flex items-center gap-2 text-2xs text-ink-subtle" role="status">
              <Spinner className="h-3 w-3" />{' '}
              {etapa === 'executando' ? traduzir('assistente.executando') : traduzir('assistente.entendendo')}
            </div>
          )}
          <div ref={fimRef} />
        </div>
      )}

      {/* ---- o que ficou pendente na conversa reaberta ---- */}
      {retomada !== null && (
        <div className={variante === 'lateral' ? 'px-4' : ''}>
          <BlocoDeRetomada retomada={retomada} />
          {/* O plano retomado não pertence a nenhum turno da lista: ele veio do
              rascunho, e não de uma mensagem. Por isso é desenhado aqui. */}
          {retomada.plano !== null && planoPendente !== null && (
            <BlocoDePlano
              plano={retomada.plano}
              ativo={estado === 'AGUARDANDO_CONFIRMACAO'}
              termoExigido={termoExigido}
              onConfirmar={() => void enviar('confirmar')}
              onCorrigir={() => void enviar('corrigir')}
              onCancelar={() => void enviar('cancelar')}
            />
          )}
        </div>
      )}

      {/* ---- opções (empresa ambígua, natureza do negócio) ---- */}
      {opcoes.length > 0 && (
        <div className={`mt-2 flex flex-wrap gap-1.5 ${variante === 'lateral' ? 'px-4' : ''}`}>
          {opcoes.map((o) => (
            <button
              key={o.valor}
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => escolher(o)}
            >
              {o.rotulo}
              {o.detalhe !== undefined && (
                <span className="ml-1 text-ink-subtle">· {o.detalhe}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* ---- campo principal ---- */}
      <form
        onSubmit={submeter}
        className={`mt-3 ${variante === 'lateral' ? 'shrink-0 border-t border-line p-4' : ''}`}
      >
        {termoExigido !== null && (
          <p className="mb-1.5 text-2xs text-warning-700">
            Ação difícil de desfazer: digite <strong>{termoExigido}</strong> para confirmar.
          </p>
        )}

        {(voz.estado === 'OUVINDO' || voz.estado === 'PEDINDO_PERMISSAO' || voz.estado === 'PARANDO') && (
          <div className="mb-1.5 flex flex-wrap items-center gap-2" role="status" aria-live="polite">
            <span className="flex items-center gap-1.5 text-2xs font-medium text-danger-700">
              <span className="h-2 w-2 animate-pulse rounded-full bg-danger-600" aria-hidden="true" />
              {voz.estado === 'PEDINDO_PERMISSAO'
                ? 'Liberando o microfone…'
                : voz.estado === 'PARANDO'
                  ? 'Finalizando…'
                  : `Gravando · ${Math.floor(voz.segundos / 60)}:${String(voz.segundos % 60).padStart(2, '0')}`}
            </span>
            {voz.estado === 'OUVINDO' && (
              <button type="button" className="btn-ghost btn-sm" onClick={voz.cancelar}>
                Cancelar
              </button>
            )}
            {voz.parcial !== '' && (
              <span className="min-w-0 flex-1 truncate text-2xs text-ink-subtle">{voz.parcial}</span>
            )}
          </div>
        )}
        {voz.mensagemDeErro !== null && voz.estado !== 'OUVINDO' && (
          <p className="mb-1.5 text-2xs text-warning-700">{voz.mensagemDeErro}</p>
        )}

        <div className="flex items-end gap-1.5">
          <textarea
            ref={campoRef}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              // Enter envia, Shift+Enter quebra linha: é a convenção que quem usa
              // chat já tem no dedo.
              if (e.key === 'Enter' && !e.shiftKey) submeter(e);
            }}
            rows={variante === 'painel-inicial' ? 2 : 2}
            placeholder="Peça para cadastrar uma empresa, criar uma oportunidade ou registrar um follow-up..."
            className="input min-h-[2.5rem] flex-1 resize-none py-2"
            aria-label="Escreva seu pedido ao Assistente Comercial"
            disabled={enviando}
          />

          {voz.suportado && (
            <button
              type="button"
              className={gravando ? 'btn-danger btn-sm' : 'btn-secondary btn-sm'}
              onClick={() => (gravando ? voz.parar() : voz.iniciar())}
              // O rótulo diz o que o clique FAZ, não o que o ícone é: enquanto
              // grava, a ação disponível é parar.
              aria-label={gravando ? 'Parar gravação' : 'Falar o pedido'}
              aria-pressed={gravando}
              title={gravando ? 'Parar gravação' : 'Falar'}
              disabled={voz.estado === 'PARANDO'}
            >
              {gravando ? <IconParar /> : <IconMicrofone />}
            </button>
          )}

          <button
            type="submit"
            className="btn-primary btn-sm"
            disabled={enviando || texto.trim() === ''}
          >
            {enviando ? <Spinner className="h-4 w-4" /> : 'Enviar'}
          </button>
        </div>

        {/* Sugestões: o jeito mais rápido de descobrir o que dá para pedir. */}
        {turnos.length === 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {sugestoes.map((s) => (
              <button
                key={s}
                type="button"
                className="rounded-full border border-line px-2.5 py-1 text-2xs text-ink-muted transition hover:border-brand-300 hover:text-brand-700"
                onClick={() => void enviar(s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </form>
    </section>
  );
}

/** Quadrado de parar: enquanto grava, é esta a ação disponível. */
function IconParar(): JSX.Element {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
      <rect x="5.5" y="5.5" width="9" height="9" rx="1.5" />
    </svg>
  );
}

/** Microfone. Local porque só o assistente usa. */
function IconMicrofone(): JSX.Element {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <rect x="7.5" y="2.5" width="5" height="9" rx="2.5" />
      <path d="M4.5 9a5.5 5.5 0 0 0 11 0M10 14.5V17M7.5 17h5" />
    </svg>
  );
}
